import { useCallback, useEffect, useState } from "react";
import { ChevronDown, CloudDownload, Loader2, Play, X } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/api";
import { useSpace } from "@/context/SpaceContext";
import { useTakeout } from "@/context/TakeoutContext";
import { useUploads } from "@/context/UploaderContext";
import { fileSize, spaceName } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { DeviceSource } from "@/components/import/DeviceSource";
import { DeviceUploadsCard } from "@/components/import/DeviceUploadsCard";

const SOURCE = { takeout: "Takeout" };
const ACTIVE = new Set(["uploading", "queued", "processing", "waiting"]);

const Stat = ({ label, value, testid }) => (
  <div className="import-stat" data-testid={testid}><b>{value ?? 0}</b><span>{label}</span></div>
);

const JobCard = ({ job, uploads, onCancel, onResume }) => {
  const mine = Object.values(uploads).filter((u) => u.jobId === job.id && u.state !== "done");
  const pct = job.total_files ? Math.round((job.processed / job.total_files) * 100) : 0;
  const reasons = Object.entries(job.skipped_reasons || {});
  return (
    <section className="import-card" data-testid={`import-job-${job.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="serif text-2xl text-neutral-900 mr-auto">{SOURCE[job.source] || "Takeout"} → {spaceName(job.space)}</h2>
        <span className={`status-pill ${job.status}`} data-testid={`import-job-status-${job.id}`}>{job.status}</span>
        {ACTIVE.has(job.status) && (
          <button className="btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1" onClick={() => onCancel(job)} data-testid={`import-job-cancel-${job.id}`}><X size={14} /> Cancel</button>
        )}
        {job.source !== "takeout" && ["cancelled", "error"].includes(job.status) && (
          <button className="btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1" onClick={() => onResume(job)} data-testid={`import-job-resume-${job.id}`}><Play size={14} /> Retry</button>
        )}
      </div>
      {mine.map((u) => (
        <div key={u.name} className="mt-4" data-testid="import-upload-progress">
          <div className="flex justify-between text-xs text-neutral-600"><span className="truncate">{u.name}</span><span>{u.state === "failed" ? `Failed — ${u.error}` : `Uploading ${Math.round(u.progress)}%`}</span></div>
          <div className="h-1 bg-neutral-200 mt-1"><div className="h-full bg-neutral-800 transition-[width] duration-300" style={{ width: `${u.progress}%` }} /></div>
        </div>
      ))}
      <div className="mt-5">
        <div className="flex justify-between text-xs text-neutral-600"><span data-testid={`import-job-progress-${job.id}`}>{job.listing ? `Finding files… ${job.listed} found so far` : `${job.processed} / ${job.total_files} files processed`}</span><span>{pct}%</span></div>
        <div className="h-1.5 bg-neutral-200 mt-1"><div className="h-full bg-neutral-900 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="import-stats">
        <Stat label="Imported" value={job.imported} testid={`import-job-imported-${job.id}`} />
        <Stat label="Duplicates hidden" value={job.duplicates_hidden} testid={`import-job-duplicates-${job.id}`} />
        <Stat label="Skipped" value={job.skipped} testid={`import-job-skipped-${job.id}`} />
        <Stat label="Albums created" value={job.albums_created} testid={`import-job-albums-${job.id}`} />
        <Stat label="Still processing" value={job.items_processing} testid={`import-job-processing-${job.id}`} />
      </div>
      {reasons.length > 0 && (
        <ul className="mt-4 text-xs text-neutral-600 space-y-1" data-testid={`import-job-skip-reasons-${job.id}`}>
          {reasons.map(([r, n]) => <li key={r}>Skipped {n} · {r}</li>)}
        </ul>
      )}
      {job.source === "takeout" && job.zips.map((z) => (
        <div key={z.upload_id} className="import-zip" data-testid={`import-zip-${z.upload_id}`}>
          <span className="truncate">{z.filename} · {fileSize(z.size)}{z.error ? ` — ${z.error}` : ""}</span>
          <span className={`status-pill ${z.status}`}>{z.status}</span>
        </div>
      ))}
    </section>
  );
};

const Expandable = ({ id, icon, title, sub, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="source-card" data-testid={`source-${id}`}>
      <button type="button" className="source-main" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`source-${id}-button`}>
        {icon}<span><b>{title}</b><small>{sub}</small></span>
        <ChevronDown size={18} className={`ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="source-body">{children}</div>}
    </section>
  );
};

export default function ImportPage() {
  const { space } = useSpace();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const { uploads } = useTakeout();
  const { entries } = useUploads();
  const [dest, setDest] = useState(space === "all" ? "private" : space);
  const [albumId, setAlbumId] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("error")) toast.error(`Couldn't connect (${params.get("error")})`);
    if ([...params.keys()].length) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => api.get("/imports").then((r) => setJobs(r.data.imports)), []);
  const active = Object.values(uploads).some((u) => u.state === "uploading") || jobs?.some((j) => ACTIVE.has(j.status) || j.items_processing > 0);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [active, load]);

  const cancel = async (job) => {
    if (!window.confirm("Cancel this import? Photos already imported stay in your library.")) return;
    await api.post(`/imports/${job.id}/cancel`);
    load();
  };
  const resume = async (job) => {
    try { await api.post(`/imports/${job.id}/resume`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const needAlbum = !admin && !albumId;

  return (
    <div className="import-wrap add-photos" data-testid="import-page">
      <h1 className="serif text-4xl sm:text-5xl text-white" data-testid="add-photos-title">Add photos</h1>
      <div className="source-list">
        <DeviceSource dest={dest} setDest={setDest} albumId={albumId} setAlbumId={setAlbumId} needAlbum={needAlbum} />
      </div>
      <h2 className="serif text-2xl text-white mt-4" data-testid="recent-imports-title">Recent imports</h2>
      <DeviceUploadsCard />
      {jobs === null && <div className="flex justify-center text-white"><Loader2 className="animate-spin" /></div>}
      {jobs?.length === 0 && !entries.length && <p className="text-white/80 text-sm" data-testid="recent-imports-empty">Nothing imported yet.</p>}
      {jobs?.map((j) => <JobCard key={j.id} job={j} uploads={uploads} onCancel={cancel} onResume={resume} />)}
    </div>
  );
}
